import React from 'react';
import { useLocation } from 'react-router';
import { constants, useAuthState, useAuthActions } from '@/utils/auth';
import { motion, AnimatePresence } from 'motion/react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaEyeSlash } from '@react-icons/all-files/fa/FaEyeSlash';
import { FaGoogle } from '@react-icons/all-files/fa/FaGoogle';
import { Field as FormikField, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Auth } from '@aws-amplify/auth';
import { PrivacyPolicyLink, TermsLink } from '@/components/LegalLinks';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import InfoCircle from '@/components/InfoCircle';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { ConnectedFieldError } from '@/components/Inputs/ConnectedFieldError';
import { takeHostedUIFailure } from '@/utils/hostedUIFailure';

// Extract the first top-level JSON object found in a string (used for embedded Cognito lambda error JSON)
function extractFirstJsonObject(raw?: string): any | undefined {
  if (!raw) return undefined;
  const start = raw.indexOf('{');
  if (start === -1) return undefined;
  let depth = 0;
  for (let i = start; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) {
        const candidate = raw.slice(start, i + 1);
        // Try direct parse
        try {
          return JSON.parse(candidate);
        } catch {
          // Sometimes it comes double-escaped. Remove backslash escapes and try again.
          try {
            const unescaped = candidate.replace(/\\"/g, '"');
            return JSON.parse(unescaped);
          } catch {
            return undefined;
          }
        }
      }
    }
  }
  return undefined;
}

function parseExpectedAuthError(
  message?: string
): { code: string; message: string } | undefined {
  const obj = extractFirstJsonObject(message);
  if (
    obj &&
    typeof obj === 'object' &&
    obj.code === 'BILDERIT_EXPECTED_ERROR'
  ) {
    return obj as any;
  }
  return undefined;
}

const signInFormSchema = Yup.object().shape({
  dni_or_email: Yup.string().required('Campo obligatorio'),
  password: Yup.string().required('Ingresa tu contraseña'),
});

const AGREE_REQUIRED_MESSAGE =
  'Debes aceptar los términos y condiciones y la política de privacidad';

const signUpFormSchema = Yup.object().shape({
  dni: Yup.string()
    .matches(/^[0-9.]*$/, 'Ingresa un DNI valido.')
    .required('Campo obligatorio'),
  first_name: Yup.string().required('Campo obligatorio'),
  last_name: Yup.string().required('Campo obligatorio'),
  email: Yup.string()
    .email('Ingresa un email válido')
    .required('Campo obligatorio'),
  // phone: Yup.string().phoneNumberStrict(),
  password: Yup.string()
    // eslint-disable-next-line no-template-curly-in-string
    .required('Campo obligatorio')
    .matches(
      /^(?=.*[a-z])[^\s]{8,}$/,
      'La contraseña debe tener al menos 8 caracteres y debe incluir al menos UNA letra minúscula.'
    ),
  pass_confirm: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden')
    .required('Campo obligatorio'),
  agree: Yup.boolean()
    .test('agree', AGREE_REQUIRED_MESSAGE, (value) => value === true)
    .required(AGREE_REQUIRED_MESSAGE),
});

const initialValues = {
  dni_or_email: '',
  email: '',
  first_name: '',
  last_name: '',
  dni: '',
  password: '',
  pass_confirm: '',
  // phone: '',
  agree: false,
};

let AuthForm: React.FC<React.ComponentProps<'div'>> = ({
  className,
  ...props
}) => {
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const togglePasswordVisible = () => setPasswordVisible((v) => !v);
  const { matches, error: authError, loading } = useAuthState();
  const { signIn, signUp, forgotPassword, resetLogin } = useAuthActions();
  const isSignup = React.useMemo(
    () => matches(constants.STATE_SIGNUP),
    [matches]
  );
  const location = useLocation();
  const initialEmail = React.useMemo(
    () => new URLSearchParams(location.search).get('email'),
    [location.search]
  );
  const initialDniOrEmail = React.useMemo(
    () => new URLSearchParams(location.search).get('u'),
    [location.search]
  );
  const initialValuesWithEmailMaybe = React.useMemo(
    () => ({
      ...initialValues,
      ...(initialEmail ? { email: initialEmail } : {}),
      ...(initialDniOrEmail ? { dni_or_email: initialDniOrEmail } : {}),
    }),
    [initialDniOrEmail, initialEmail]
  );

  const schema = React.useMemo(
    () => (isSignup ? signUpFormSchema : signInFormSchema),
    [isSignup]
  );

  const expectedAuthError = React.useMemo(
    () => parseExpectedAuthError(authError?.message),
    [authError?.message]
  );

  // A sign-in through the hosted UI (Google) that failed comes back to a new
  // page: its message shows here until the person tries again.
  const [hostedUIFailure, setHostedUIFailure] =
    React.useState(takeHostedUIFailure);
  const hostedUIError = React.useMemo(
    () =>
      hostedUIFailure &&
      (parseExpectedAuthError(hostedUIFailure)?.message ?? hostedUIFailure),
    [hostedUIFailure]
  );

  return (
    <Formik
      initialValues={initialValuesWithEmailMaybe}
      enableReinitialize
      validationSchema={schema}
      onSubmit={(values) => {
        setHostedUIFailure(null);
        const {
          dni_or_email,
          password,
          email,
          first_name,
          last_name,
          // phone: formattedPhoneNumber,
        } = values;

        // const phone = formattedPhoneNumber?.replace(/ /g, '') ?? undefined;
        let dni = values.dni.replace(/\./g, '');
        if (matches(constants.STATE_SIGNUP)) {
          signUp(dni, password, email, {
            first_name,
            last_name,
            dni,
            // ...(phone ? { phone } : {}),
          } as any);
        }
        if (matches(constants.STATE_SIGNIN)) {
          signIn(dni_or_email, password);
        }
      }}
    >
      {() => (
        <div className={cn('flex flex-col gap-6', className)} {...props}>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Bienvenido</CardTitle>
              <CardDescription>
                Ingresa con tu cuenta de Bilderit
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form className="flex flex-col gap-6">
                {!isSignup && (
                  <FormikField name="dni_or_email">
                    {({ field, meta }: FieldProps) => (
                      <Field data-invalid={!!meta.error && meta.touched}>
                        <FieldLabel
                          htmlFor={field.name}
                          className="flex flex-row gap-2"
                        >
                          Usuario
                          <InfoCircle details="Usuario, DNI o Email. Para operarios gestionados, se sugiere usar las tres primeras letras de la compañia seguidas del DNI (ejemplo bil-12345678)." />
                        </FieldLabel>
                        <Input
                          {...field}
                          id={field.name}
                          aria-invalid={
                            (!!meta.error && meta.touched) || undefined
                          }
                          type="text"
                          autoComplete="email"
                        />
                        <ConnectedFieldError name={field.name} />
                      </Field>
                    )}
                  </FormikField>
                )}
                <AnimatePresence>
                  {isSignup && (
                    <motion.div
                      key="signup-fields"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden flex flex-col gap-6"
                    >
                      <FormikField name="email">
                        {({ field, meta }: FieldProps) => (
                          <Field data-invalid={!!meta.error && meta.touched}>
                            <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                            <Input
                              {...field}
                              id={field.name}
                              aria-invalid={
                                (!!meta.error && meta.touched) || undefined
                              }
                              type="email"
                            />
                            <ConnectedFieldError name={field.name} />
                          </Field>
                        )}
                      </FormikField>
                      <FormikField name="dni">
                        {({ field, meta }: FieldProps) => (
                          <Field data-invalid={!!meta.error && meta.touched}>
                            <FieldLabel htmlFor={field.name}>DNI</FieldLabel>
                            <Input
                              {...field}
                              id={field.name}
                              aria-invalid={
                                (!!meta.error && meta.touched) || undefined
                              }
                              type="text"
                            />
                            <ConnectedFieldError name={field.name} />
                          </Field>
                        )}
                      </FormikField>
                      <FormikField name="first_name">
                        {({ field, meta }: FieldProps) => (
                          <Field data-invalid={!!meta.error && meta.touched}>
                            <FieldLabel htmlFor={field.name}>Nombre</FieldLabel>
                            <Input
                              {...field}
                              id={field.name}
                              aria-invalid={
                                (!!meta.error && meta.touched) || undefined
                              }
                              type="text"
                              autoComplete="given-name"
                            />
                            <ConnectedFieldError name={field.name} />
                          </Field>
                        )}
                      </FormikField>
                      <FormikField name="last_name">
                        {({ field, meta }: FieldProps) => (
                          <Field data-invalid={!!meta.error && meta.touched}>
                            <FieldLabel htmlFor={field.name}>
                              Apellido
                            </FieldLabel>
                            <Input
                              {...field}
                              id={field.name}
                              aria-invalid={
                                (!!meta.error && meta.touched) || undefined
                              }
                              type="text"
                              autoComplete="family-name"
                            />
                            <ConnectedFieldError name={field.name} />
                          </Field>
                        )}
                      </FormikField>
                    </motion.div>
                  )}
                </AnimatePresence>
                <FormikField name="password">
                  {({ field, meta }: FieldProps) => (
                    <Field
                      data-invalid={!!meta.error && meta.touched}
                      className="relative"
                    >
                      <FieldLabel htmlFor={field.name}>Contraseña</FieldLabel>
                      <InputGroup>
                        <InputGroupInput
                          {...field}
                          id={field.name}
                          aria-invalid={
                            (!!meta.error && meta.touched) || undefined
                          }
                          type={passwordVisible ? 'text' : 'password'}
                          autoComplete={
                            matches(constants.STATE_SIGNIN)
                              ? 'current-password'
                              : 'new-password'
                          }
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupButton
                            size={'icon-sm'}
                            variant={'ghost'}
                            onClick={togglePasswordVisible}
                          >
                            {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                          </InputGroupButton>
                        </InputGroupAddon>
                      </InputGroup>
                      {/* Lo ponemos al final para que tenga un orden mas logico al moverse con tab */}
                      {!isSignup && (
                        <Button
                          variant="link"
                          size="sm"
                          type="button"
                          onClick={forgotPassword as any}
                          className="absolute top-0 justify-end text-xs underline-offset-4 hover:underline h-auto p-0"
                        >
                          Olvidaste tu contraseña?
                        </Button>
                      )}
                      <ConnectedFieldError name={field.name} />
                    </Field>
                  )}
                </FormikField>
                <AnimatePresence>
                  {isSignup && (
                    <motion.div
                      key="signup-confirm-fields"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden flex flex-col gap-6"
                    >
                      <FormikField name="pass_confirm">
                        {({ field, meta }: FieldProps) => (
                          <Field data-invalid={!!meta.error && meta.touched}>
                            <FieldLabel htmlFor={field.name}>
                              Confirmar Contraseña
                            </FieldLabel>

                            <InputGroup>
                              <InputGroupInput
                                {...field}
                                id={field.name}
                                aria-invalid={
                                  (!!meta.error && meta.touched) || undefined
                                }
                                type={passwordVisible ? 'text' : 'password'}
                                autoComplete="new-password"
                              />
                              <InputGroupAddon align="inline-end">
                                <Button
                                  size={'sm-icon'}
                                  variant={'ghost'}
                                  onClick={togglePasswordVisible}
                                >
                                  {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                                </Button>
                              </InputGroupAddon>
                            </InputGroup>
                            <ConnectedFieldError name={field.name} />
                          </Field>
                        )}
                      </FormikField>
                      <FormikField name="agree">
                        {({ field, form, meta }: FieldProps) => (
                          <div className={cn('grid gap-2')}>
                            <div
                              className={cn(
                                'flex items-center gap-2',
                                meta.error && meta.touched && 'peer invalid'
                              )}
                            >
                              <Checkbox
                                id={field.name}
                                {...field}
                                onCheckedChange={(checked) =>
                                  form.setFieldValue(field.name, checked)
                                }
                              />
                              <FieldLabel
                                htmlFor={field.name}
                                className={cn(
                                  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                                  meta.error && meta.touched && 'peer invalid'
                                )}
                              >
                                {/* One span, so the flex label doesn't split the sentence into columns */}
                                <span className="leading-snug">
                                  Acepto los{' '}
                                  <TermsLink>términos y condiciones</TermsLink>{' '}
                                  y la{' '}
                                  <PrivacyPolicyLink>
                                    política de privacidad
                                  </PrivacyPolicyLink>
                                </span>
                              </FieldLabel>
                            </div>
                            <ConnectedFieldError name={field.name} />
                          </div>
                        )}
                      </FormikField>
                    </motion.div>
                  )}
                </AnimatePresence>
                <AlertDialog
                  open={matches({
                    [constants.STATE_SIGNIN]: 'maybe_email_not_confirmed',
                  })}
                  onOpenChange={() => {
                    resetLogin();
                    (
                      document.getElementById(
                        'dni_or_email'
                      ) as HTMLInputElement | null
                    )?.select();
                  }}
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Problemas</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tu correo no se encontró en nuestro sistema. Si es el
                        que usaste para registrarte, pero aún no lo verificaste,
                        intenta loguearte con tu DNI para realizar la
                        verificación.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cerrar</AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {(expectedAuthError || authError || hostedUIError) && (
                  <span style={{ color: 'red' }} role="alert">
                    {expectedAuthError?.message ||
                      authError?.message
                        .replace('username', 'DNI')
                        .replace(
                          /.*User does not exist.*/,
                          'No se encontró tu usuario.'
                        )
                        .replace(
                          /.*PreSignUp failed with error User already registered.*/,
                          'El email seleccionado ya está en uso.'
                        )
                        .replace(
                          /.*User already exists.*/,
                          'El DNI seleccionado ya está en uso.'
                        )
                        .replace(
                          /.*Incorrect DNI or password.*/,
                          'Usuario o contraseña incorrectos.'
                        ) ||
                      hostedUIError}
                  </span>
                )}
                <Button
                  type="submit"
                  className={cn('w-full')}
                  disabled={loading}
                >
                  {loading && <LoadingSpinner />}
                  {!loading && isSignup
                    ? buttonText[constants.STATE_SIGNUP]
                    : buttonText[constants.STATE_SIGNIN]}
                </Button>
                <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                  <span className="relative z-10 bg-background px-2 text-muted-foreground">
                    O continuar con
                  </span>
                </div>
                <Button
                  variant="outline"
                  className={cn('w-full')}
                  onClick={() => {
                    setHostedUIFailure(null);
                    Auth.federatedSignIn({ provider: 'Google' } as any);
                  }}
                >
                  <FaGoogle />
                </Button>
                <div className="text-center text-sm">
                  {isSignup ? 'Ya' : 'No'} tenés una cuenta?{' '}
                  <Button
                    variant="link"
                    onClick={isSignup ? (signIn as any) : (signUp as any)}
                    className="underline underline-offset-4 p-0"
                  >
                    {
                      buttonText[
                        isSignup
                          ? constants.STATE_SIGNIN
                          : constants.STATE_SIGNUP
                      ]
                    }
                  </Button>
                </div>
              </Form>
            </CardContent>
          </Card>
        </div>
      )}
    </Formik>
  );
};

export default AuthForm;

const buttonText = {
  [constants.STATE_SIGNIN]: 'Entrar',
  [constants.STATE_SIGNUP]: 'Registrarse',
};
